/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: Presentational
 * Reason: The German scope badge of one row of an exchange config carrier — „Vorgangstyp" for a row
 *         the case type owns, „Partei-Version" for one it inherits. Reads the widget table row it is
 *         handed, formats nothing else, holds no state and calls no service.
 */
import {Component, Input} from '@angular/core';
import {
    CW_SCOPE_LABEL_CASE_TYPE,
    CW_SCOPE_LABEL_PARTY_VERSION,
    cwCarrierRowPartyVersionName,
    cwCarrierRowScope,
} from '../../../../../../widget/cw-carrier-scope.util';

/**
 * #C.1 (cw_sales_enhancements_round_2) — THE „woher kommt diese Zeile" badge of the two exchange
 * config carriers ({@code cw.CwCaseTypeExchangeEchoRule},
 * {@code cw.CwCaseTypeExternalMessageMapping}).
 *
 * <h3>The gap it closes</h3>
 * Since #C.1 a carrier row hangs on ONE case type OR on ONE {@code cc.ExternalPartyVersion} (XOR),
 * and the case-type tables show the UNION of both. Without a visible mark an inherited row — which
 * belongs to every case type of that version stream and must not be edited from one of them — looks
 * exactly like a local one.
 *
 * <h3>Why it is a component and not markup in the two tab templates</h3>
 * The same badge is wanted in two places (Bipro → Echo-Regeln, Weitere → Meldungs-Mapping), and a
 * copied block plus its four reader methods is two renderings of one concept that drift on the first
 * change — the same reasoning that produced {@code CwCaseFieldEventLineComponent}. Everything it
 * knows arrives through {@link object}; there is no second source.
 *
 * <h3>What „object" is</h3>
 * The row a {@code mvs-widget-custom-field-directive} template is handed. Its shape differs between
 * widget types, so the reading is done by {@link cwCarrierRowScope} /
 * {@link cwCarrierRowPartyVersionName}, which walk the four known shapes — a wrong guess renders an
 * empty cell with no error, so the walk lives in ONE place and is covered by
 * {@code cw-carrier-scope.util.spec.ts}.
 *
 * <h3>The fail-safe direction</h3>
 * A row is called inherited ONLY when the party-version join actually resolved. „Nicht geerbt" is the
 * safe default: a row wrongly badged as inherited would tell a user not to edit something they own.
 */
@Component({
    selector: 'mvs-cw-carrier-scope-badge',
    templateUrl: './cw-carrier-scope-badge.component.html',
    standalone: false,
})
export class CwCarrierScopeBadgeComponent {

    /** The widget table row to classify. */
    @Input() object: any;

    /** True when the row is INHERITED from the case type's Partei-Version. */
    isInherited(): boolean {
        return cwCarrierRowScope(this.object) === 'partyVersion';
    }

    /** German badge text — „Vorgangstyp" (own) vs „Partei-Version" (inherited). */
    label(): string {
        return this.isInherited() ? CW_SCOPE_LABEL_PARTY_VERSION : CW_SCOPE_LABEL_CASE_TYPE;
    }

    /** German tooltip — names the version stream an inherited row comes from, where resolvable. */
    tooltip(): string {
        if (!this.isInherited()) {
            return 'Diese Zeile gehört diesem Vorgangstyp.';
        }
        const versionName = cwCarrierRowPartyVersionName(this.object);
        return versionName
            ? 'Geerbt von der Partei-Version „' + versionName + '“ — gilt für alle Vorgangstypen dieser Version.'
            : 'Geerbt von der Partei-Version — gilt für alle Vorgangstypen dieser Version.';
    }
}
